import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Network, 
  X, 
  ZoomIn, 
  ZoomOut, 
  Maximize2,
  Info
} from 'lucide-react';
import { frameworkGraph, getFrameworkById } from '../data/relationshipGraph';
import type { FrameworkMetadata, RelationshipType } from '../types/relationships';

interface RelationshipGraphProps {
  frameworkId: string;
  maxDepth?: number;
  className?: string;
}

interface GraphNode {
  id: string;
  x: number;
  y: number;
  metadata: FrameworkMetadata;
  depth: number;
}

interface GraphEdge {
  from: string;
  to: string;
  type: RelationshipType;
  strength: number;
}

export const RelationshipGraph: React.FC<RelationshipGraphProps> = ({
  frameworkId,
  maxDepth = 2,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const svgRef = useRef<SVGSVGElement>(null);

  const { nodes, edges } = buildGraph(frameworkId, maxDepth);

  if (nodes.length === 0) return null;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));

  return (
    <div className={`bg-[var(--bg-raised)]  shadow-lg border-2 border-sage-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-sage-50 to-primary-50 border-b-2 border-sage-200">
        <div className="flex items-center gap-3">
          <Network className="h-6 w-6 text-sage-600" />
          <div>
            <h3 className="font-bold text-charcoal-900">Relationship Network</h3>
            <p className="text-xs text-charcoal-600">
              {nodes.length} connected framework{nodes.length === 1 ? '' : 's'}, {edges.length} relationship{edges.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-sage-100 rounded transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4 text-charcoal-600" />
          </button>
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-sage-100 rounded transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4 text-charcoal-600" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-sage-100 rounded transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <X className="h-4 w-4 text-charcoal-600" />
            ) : (
              <Maximize2 className="h-4 w-4 text-charcoal-600" />
            )}
          </button>
        </div>
      </div>

      {/* Graph Canvas */}
      <div className={`relative bg-gradient-to-br from-slate-50 to-charcoal-50 ${isExpanded ? 'h-[600px]' : 'h-[400px]'}`}>
        <svg
          ref={svgRef}
          className="w-full h-full"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
        >
          {/* Edges */}
          <g className="edges">
            {edges.map((edge, idx) => {
              const fromNode = nodes.find(n => n.id === edge.from);
              const toNode = nodes.find(n => n.id === edge.to);
              if (!fromNode || !toNode) return null;

              return (
                <g key={idx}>
                  <line
                    x1={fromNode.x}
                    y1={fromNode.y}
                    x2={toNode.x}
                    y2={toNode.y}
                    stroke={getEdgeColor(edge.type)}
                    strokeWidth={2 + edge.strength * 2}
                    strokeOpacity={0.6}
                    markerEnd="url(#arrowhead)"
                  />
                  {/* Edge label */}
                  <text
                    x={(fromNode.x + toNode.x) / 2}
                    y={(fromNode.y + toNode.y) / 2}
                    className="text-xs fill-charcoal-600"
                    textAnchor="middle"
                    dy="-5"
                  >
                    {getRelationshipLabel(edge.type)}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Nodes */}
          <g className="nodes">
            {nodes.map((node) => {
              const isCenter = node.id === frameworkId;
              const isSelected = node.id === selectedNode;
              const isHovered = node.id === hoveredNode;
              
              return (
                <g
                  key={node.id}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={() => setSelectedNode(node.id === selectedNode ? null : node.id)}
                  className="cursor-pointer"
                >
                  {/* Node circle */}
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={isCenter ? 40 : isSelected || isHovered ? 35 : 30}
                    fill={isCenter ? '#627d98' : isSelected ? '#8cb299' : '#f0f4f8'}
                    stroke={isCenter ? '#334e68' : isSelected ? '#526b5c' : '#bcccdc'}
                    strokeWidth={isCenter ? 3 : isSelected || isHovered ? 2.5 : 2}
                    className="transition-all duration-200"
                  />
                  {/* Node label */}
                  <text
                    x={node.x}
                    y={node.y}
                    className={`text-xs font-semibold pointer-events-none ${
                      isCenter ? 'fill-white' : isSelected ? 'fill-charcoal-900' : 'fill-charcoal-700'
                    }`}
                    textAnchor="middle"
                    dy="0.3em"
                  >
                    {truncateText(node.metadata.name, 12)}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#9fb3c8" />
            </marker>
          </defs>
        </svg>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-[var(--bg-raised)] shadow-md border border-[var(--rule)] p-3 text-xs">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-3 w-3 text-charcoal-600" />
            <span className="font-semibold text-charcoal-900">Legend</span>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-primary-600 border-2 border-primary-800"></div>
              <span className="text-charcoal-700">Center (Current)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-primary-50 border-2 border-primary-300"></div>
              <span className="text-charcoal-700">Connected</span>
            </div>
          </div>
        </div>

        {/* Node Details Panel */}
        {selectedNode && (
          <div className="absolute top-4 right-4 bg-[var(--bg-raised)] shadow-lg border-2 border-sage-300 p-4 max-w-xs">
            <NodeDetails
              frameworkId={selectedNode}
              onClose={() => setSelectedNode(null)}
            />
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-sage-50 px-4 py-3 border-t border-sage-200">
        <p className="text-xs text-charcoal-600 text-center">
          <span className="font-semibold">Tip:</span> Click nodes to see details. Hover to highlight. Use zoom controls to adjust view.
        </p>
      </div>
    </div>
  );
};

// Node Details Component
interface NodeDetailsProps {
  frameworkId: string;
  onClose: () => void;
}

const NodeDetails: React.FC<NodeDetailsProps> = ({ frameworkId, onClose }) => {
  const framework = getFrameworkById(frameworkId);
  
  if (!framework) return null;

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <h4 className="font-bold text-charcoal-900 text-sm">{framework.name}</h4>
        <button
          onClick={onClose}
          className="p-1 hover:bg-charcoal-100 rounded transition-colors"
        >
          <X className="h-3 w-3 text-charcoal-600" />
        </button>
      </div>
      
      <div className="space-y-2 text-xs">
        <div>
          <span className="font-semibold text-charcoal-700">Category:</span>
          <span className="ml-2 text-charcoal-600">{framework.category}</span>
        </div>
        
        {framework.tags.scope && (
          <div>
            <span className="font-semibold text-charcoal-700">Scope:</span>
            <span className="ml-2 text-charcoal-600">{framework.tags.scope}</span>
          </div>
        )}
        
        <div>
          <span className="font-semibold text-charcoal-700">Difficulty:</span>
          <span className="ml-2 text-charcoal-600">{framework.difficulty}</span>
        </div>
      </div>

      <Link
        to={`/elevation/entry/${framework.encodedId}`}
        className="mt-3 block w-full px-3 py-2 bg-sage-600 hover:bg-sage-700 text-white text-xs font-medium rounded text-center transition-colors"
      >
        View Full Details →
      </Link>
    </div>
  );
};

// Helper Functions

function buildGraph(centerFrameworkId: string, maxDepth: number): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const visited = new Set<string>();
  
  const centerFramework = getFrameworkById(centerFrameworkId);
  if (!centerFramework) return { nodes, edges };

  // Add center node
  nodes.push({
    id: centerFrameworkId,
    x: 400,
    y: 200,
    metadata: centerFramework,
    depth: 0,
  });
  visited.add(centerFrameworkId);

  // BFS to build graph
  const queue: Array<{ id: string; depth: number }> = [{ id: centerFrameworkId, depth: 0 }];
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.depth >= maxDepth) continue;

    const relationships = frameworkGraph[current.id] || [];
    const angle = (2 * Math.PI) / Math.max(relationships.length, 1);
    
    relationships.forEach((rel, idx) => {
      if (!visited.has(rel.targetId)) {
        const framework = getFrameworkById(rel.targetId);
        if (!framework) return;

        // Calculate position in a circular layout
        const radius = 150 + (current.depth + 1) * 100;
        const nodeAngle = angle * idx + (current.depth * Math.PI / 4);
        const x = 400 + radius * Math.cos(nodeAngle);
        const y = 200 + radius * Math.sin(nodeAngle);

        nodes.push({
          id: rel.targetId,
          x,
          y,
          metadata: framework,
          depth: current.depth + 1,
        });
        
        visited.add(rel.targetId);
        queue.push({ id: rel.targetId, depth: current.depth + 1 });
      }

      // Add edge
      edges.push({
        from: current.id,
        to: rel.targetId,
        type: rel.type,
        strength: rel.strength,
      });
    });
  }

  return { nodes, edges };
}

function getEdgeColor(type: RelationshipType): string {
  const colors: Record<RelationshipType, string> = {
    'prerequisite': '#f59187',
    'extends': '#627d98',
    'contains': '#8cb299',
    'complement': '#f5b969',
    'derived-from': '#919ba5',
    'applies-to': '#6B8E23',
    'alternative': '#8E4585',
    'conflicts': '#E97451',
  };
  return colors[type] || '#9fb3c8';
}

function getRelationshipLabel(type: RelationshipType): string {
  const labels: Record<RelationshipType, string> = {
    'prerequisite': '→',
    'extends': '↗',
    'contains': '⊃',
    'complement': '⇄',
    'derived-from': '⤴',
    'applies-to': '→',
    'alternative': '⟷',
    'conflicts': '⚡',
  };
  return labels[type] || '→';
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 1) + '…';
}

